import React, { useEffect, useMemo, useState } from "react";
import PublicLayout from "../components/PublicLayout";
import Seo from "../components/Seo";
import InfinityMotif from "../components/InfinityMotif";
import { api, imageUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, MagnifyingGlass, Star } from "@phosphor-icons/react";

export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/offers")
      .then((r) => setOffers(r.data || []))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, []);

  const providers = useMemo(() => {
    const set = new Set(offers.map((o) => o.provider));
    return ["all", ...Array.from(set)];
  }, [offers]);

  const filtered = offers.filter((o) => {
    const matchesProv = filter === "all" || o.provider === filter;
    const matchesQ =
      !q ||
      o.title.toLowerCase().includes(q.toLowerCase()) ||
      o.description.toLowerCase().includes(q.toLowerCase());
    return matchesProv && matchesQ;
  });

  return (
    <PublicLayout>
      <Seo
        title="Special Offers"
        description="Negotiated deals from MTN, Vodacom, Cell C, Vox and Telkom — updated weekly. Browse current specials or build a custom package."
        path="/offers"
      />
      {/* Hero */}
      <section className="relative pt-20 pb-24 lg:pb-28 overflow-hidden text-center" data-testid="offers-page">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[90vw] h-[340px] rainbow-swoosh pointer-events-none" />
        <InfinityMotif variant="wireframe" className="absolute top-2 left-1/2 -translate-x-1/2 w-[1100px] max-w-[120vw] h-auto" opacity={0.14} />
        <div className="relative max-w-[1000px] mx-auto px-6">
          <div className="flex justify-center mb-7">
            <Star size={24} weight="fill" className="text-[#dda632]" />
          </div>
          <div className="mx-auto mb-8 w-32 h-[5px] rounded-full bg-gradient-to-r from-[#55a2d2] via-[#af3fa6] to-[#dda632]" />
          <div className="eyebrow mb-5">Special Offers</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.4rem] leading-[0.98] max-w-4xl mx-auto">
            Negotiated deals,<br />
            <span className="rainbow-text">every major SA carrier.</span>
          </h1>
          <p className="mt-10 text-xl text-[#a8b2cc] leading-relaxed max-w-2xl mx-auto">
            Updated weekly with fresh promotions from MTN, Vodacom, Cell C, Vox and Telkom. Or let
            us build a custom package — it's faster than you'd expect.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/build-package" className="btn-primary" data-testid="offers-build-package-cta">
              Build Your Own Package <ArrowUpRight size={14} weight="bold" />
            </Link>
            <a href="#offers-grid" className="btn-outline">Browse deals</a>
          </div>
        </div>
      </section>

      <div className="rainbow-bar rainbow-bar-xl" />

      {/* Filters */}
      <section className="sticky top-[78px] z-30 bg-[#070b14]/92 backdrop-blur-xl border-b border-[#2c3854]">
        <div className="max-w-[1180px] mx-auto px-6 py-5 flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {providers.map((p) => (
              <button
                key={p}
                onClick={() => setFilter(p)}
                data-testid={`offer-filter-${p.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-5 py-2 rounded-full text-[0.7rem] uppercase tracking-[0.22em] font-mono-accent font-semibold border transition-colors ${
                  filter === p
                    ? "bg-[#141e32] text-white border-[#f5ecd9]"
                    : "bg-[#f5ecd9] text-[#070b14] border-[#2c3854] hover:border-[#f5ecd9]"
                }`}
              >
                {p === "all" ? "All Deals" : p}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 border border-[#2c3854] rounded-full px-4 py-2 bg-[#141e32] focus-within:border-[#f5ecd9] transition-colors">
            <MagnifyingGlass size={15} className="text-[#a8b2cc]" />
            <input
              type="text"
              placeholder="Search deals..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="bg-transparent outline-none text-sm w-48"
              data-testid="offer-search-input"
            />
          </div>
        </div>
      </section>

      {/* Offers grid */}
      <section id="offers-grid" className="py-24">
        <div className="max-w-[1180px] mx-auto px-6">
          {loading ? (
            <div className="text-center py-20 font-mono-accent text-[#a8b2cc]">Loading offers…</div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center max-w-xl mx-auto">
              <div className="font-display text-4xl mb-4">No offers match.</div>
              <p className="text-[#a8b2cc] mb-8 text-lg">
                We're brewing new deals. Or let us build a custom package tailored to you.
              </p>
              <Link to="/build-package" className="btn-primary" data-testid="offers-empty-cta">
                Build Your Own Package <ArrowUpRight size={14} weight="bold" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((o) => (
                <article key={o.id} className="soft-card group flex flex-col" data-testid={`offer-card-${o.id}`}>
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#141e32]">
                    {o.image_path ? (
                      <img
                        src={imageUrl(o.image_path)}
                        alt={o.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-mono-accent text-[#a8b2cc]">
                        {o.provider.toUpperCase()}
                      </div>
                    )}
                    {o.badge && (
                      <span className="absolute top-4 left-4 brand-chip bg-[#af3fa6] text-white">
                        {o.badge}
                      </span>
                    )}
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="eyebrow mb-3">{o.provider}</div>
                    <h3 className="font-display text-2xl text-[#f5ecd9] mb-3 leading-tight">{o.title}</h3>
                    <p className="text-[#a8b2cc] leading-relaxed whitespace-pre-line flex-1">{o.description}</p>
                    <div className="mt-6 flex items-center justify-between border-t border-[#2c3854] pt-5">
                      {o.price ? (
                        <div className="font-display text-xl">{o.price}</div>
                      ) : (
                        <span />
                      )}
                      <Link to={`/offers/${o.id}`} className="btn-link" data-testid={`offer-enquire-${o.id}`}>
                        View deal <ArrowUpRight size={12} weight="bold" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
